import {
  MetadataProcessingError,
  processMetadataInWorker,
  type InspectedMetadata,
} from '@/features/image-metadata-cleaner'
import {
  OpenImageViewerError,
  type OpenImageViewerLoadedImage,
} from '@/features/open-image-viewer'

export type WebpExportFormat = 'jpg' | 'png'

export interface WebpDimensions {
  width: number
  height: number
}

export interface WebpLoadErrors {
  corrupt: string
  decodeFailure: string
}

const HEADER_READ_SIZE = 30
const WEBP_TEST_IMAGE =
  'data:image/webp;base64,UklGRiIAAABXRUJQVlA4IBYAAAAwAQCdASoBAAEADMDOJaQAA3AA/v89WAAAAA=='

let webpDecodeSupport: Promise<boolean> | null = null

function fourCc(bytes: Uint8Array, offset: number): string {
  return String.fromCharCode(...bytes.subarray(offset, offset + 4))
}

function uint24(bytes: Uint8Array, offset: number): number {
  return bytes[offset]! | (bytes[offset + 1]! << 8) | (bytes[offset + 2]! << 16)
}

export function parseWebpDimensions(
  bytes: Uint8Array,
  totalFileSize = bytes.byteLength,
): WebpDimensions | null {
  if (totalFileSize < 20 || bytes.byteLength < 20) return null
  if (fourCc(bytes, 0) !== 'RIFF' || fourCc(bytes, 8) !== 'WEBP') return null

  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  const riffSize = view.getUint32(4, true)
  const riffEnd = riffSize + 8
  if (riffEnd < 20 || riffEnd !== totalFileSize) return null

  const chunkSize = view.getUint32(16, true)
  const paddedChunkSize = chunkSize + (chunkSize & 1)
  const chunkEnd = 20 + paddedChunkSize
  if (chunkEnd > riffEnd || chunkEnd > totalFileSize) return null

  const chunk = fourCc(bytes, 12)
  if (chunk === 'VP8X') {
    if (chunkSize !== 10 || bytes.byteLength < 30) return null
    return { width: uint24(bytes, 24) + 1, height: uint24(bytes, 27) + 1 }
  }
  if (chunk === 'VP8L') {
    if (chunkSize < 5 || bytes.byteLength < 25 || bytes[20] !== 0x2f) return null
    return {
      width: 1 + bytes[21]! + ((bytes[22]! & 0x3f) << 8),
      height: 1 + (bytes[22]! >> 6) + (bytes[23]! << 2) + ((bytes[24]! & 0x0f) << 10),
    }
  }
  if (chunk === 'VP8 ') {
    if (
      chunkSize < 10 ||
      bytes.byteLength < 30 ||
      bytes[23] !== 0x9d ||
      bytes[24] !== 0x01 ||
      bytes[25] !== 0x2a
    ) return null
    return {
      width: view.getUint16(26, true) & 0x3fff,
      height: view.getUint16(28, true) & 0x3fff,
    }
  }
  return null
}

export async function validateWebp(
  file: File,
  unsupportedMessage: string,
  tooLargeMessage: string,
  maxPixels: number,
  canDecode: () => Promise<boolean> = canDecodeWebp,
  inspect: (bytes: Uint8Array, signal: AbortSignal) => Promise<InspectedMetadata> =
    (bytes, signal) => processMetadataInWorker(bytes, 'inspect', signal),
  signal?: AbortSignal,
): Promise<boolean> {
  if (signal?.aborted) throw createAbortError()
  const header = new Uint8Array(await file.slice(0, HEADER_READ_SIZE).arrayBuffer())
  if (signal?.aborted) throw createAbortError()
  const dimensions = parseWebpDimensions(header, file.size)
  if (!dimensions?.width || !dimensions.height) return false
  if (dimensions.width * dimensions.height > maxPixels) {
    throw new OpenImageViewerError(tooLargeMessage, 'too_large_image')
  }
  try {
    const bytes = new Uint8Array(await file.arrayBuffer())
    if (signal?.aborted) throw createAbortError()
    const inspected = await inspect(
      bytes,
      signal ?? new AbortController().signal,
    )
    if (inspected.format !== 'webp') return false
  } catch (error) {
    if (isAbortError(error)) throw error
    if (
      (error instanceof MetadataProcessingError &&
        error.code === 'too_large_dimensions') ||
      (error instanceof Error && error.message === 'too_large_dimensions')
    ) {
      throw new OpenImageViewerError(tooLargeMessage, 'too_large_image')
    }
    return false
  }
  if (signal?.aborted) throw createAbortError()
  if (!(await canDecode())) {
    throw new OpenImageViewerError(unsupportedMessage, 'decode_failure')
  }
  return true
}

function createAbortError(): DOMException {
  return new DOMException('Operation aborted', 'AbortError')
}

function isAbortError(error: unknown): error is DOMException {
  return error instanceof DOMException && error.name === 'AbortError'
}

export function canDecodeWebp(): Promise<boolean> {
  if (!webpDecodeSupport) {
    webpDecodeSupport = new Promise((resolve) => {
      const image = new Image()
      image.onload = () => resolve(image.naturalWidth > 0 && image.naturalHeight > 0)
      image.onerror = () => resolve(false)
      image.src = WEBP_TEST_IMAGE
    })
  }
  return webpDecodeSupport
}

export function resetWebpDecodeSupportForTests(): void {
  webpDecodeSupport = null
}

export function loadWebpImage(
  file: File,
  errors: WebpLoadErrors,
  signal?: AbortSignal,
): Promise<OpenImageViewerLoadedImage> {
  if (signal?.aborted) return Promise.reject(createAbortError())
  const previewUrl = URL.createObjectURL(file)

  return new Promise((resolve, reject) => {
    const image = new Image()
    const cleanup = (): void => {
      image.onload = null
      image.onerror = null
      signal?.removeEventListener('abort', abort)
    }
    const release = (): void => {
      image.src = ''
      URL.revokeObjectURL(previewUrl)
    }
    const abort = (): void => {
      cleanup()
      release()
      reject(createAbortError())
    }

    image.onload = () => {
      cleanup()
      const width = image.naturalWidth
      const height = image.naturalHeight
      if (!width || !height) {
        release()
        reject(new OpenImageViewerError(errors.corrupt, 'corrupt'))
        return
      }
      resolve({
        previewUrl,
        element: image,
        width,
        height,
        mimeLabel: file.type || 'image/webp',
        formatLabel: 'WebP',
        revoke: release,
      })
    }
    image.onerror = () => {
      cleanup()
      release()
      reject(new OpenImageViewerError(errors.decodeFailure, 'decode_failure'))
    }
    signal?.addEventListener('abort', abort, { once: true })
    image.src = previewUrl
  })
}

export interface WebpExportOptions {
  element: HTMLImageElement
  width: number
  height: number
  filename: string
  format: WebpExportFormat
  isActive: () => boolean
  createCanvas?: () => HTMLCanvasElement
  download?: (blob: Blob, filename: string) => void
  onSuccess?: () => void
}

export async function exportWebpImage(options: WebpExportOptions): Promise<boolean> {
  const canvas = options.createCanvas?.() ?? document.createElement('canvas')
  canvas.width = options.width
  canvas.height = options.height
  const context = canvas.getContext('2d')
  if (!context) throw new Error('Canvas context is unavailable')

  if (options.format === 'jpg') {
    context.fillStyle = '#ffffff'
    context.fillRect(0, 0, canvas.width, canvas.height)
  }
  context.drawImage(options.element, 0, 0)

  const mime = options.format === 'jpg' ? 'image/jpeg' : 'image/png'
  const blob = await canvasToBlob(canvas, mime, options.format === 'jpg' ? 0.92 : undefined)
  if (!options.isActive()) return false
  const download = options.download ?? downloadBlob
  download(blob, outputFilename(options.filename, options.format))
  if (!options.isActive()) return false
  options.onSuccess?.()
  return true
}

export function canvasToBlob(
  canvas: HTMLCanvasElement,
  mimeType: string,
  quality?: number,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    try {
      canvas.toBlob((blob) => {
        if (!blob) return reject(new Error('Canvas export returned no data'))
        if (blob.type.toLowerCase() !== mimeType.toLowerCase()) {
          return reject(new Error(`Canvas export returned ${blob.type || 'unknown MIME'}`))
        }
        resolve(blob)
      }, mimeType, quality)
    } catch (error) {
      reject(error)
    }
  })
}

export function outputFilename(filename: string, format: WebpExportFormat): string {
  return `${filename.replace(/\.[^.]+$/, '') || 'image'}.${format}`
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  window.setTimeout(() => URL.revokeObjectURL(url), 0)
}
